import React, { useCallback, useEffect, useState } from "react";
import {
    Autocomplete, Box, CircularProgress, IconButton, Table, TableBody, TableCell,
    TableHead, TableRow, Tab, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import ChevronRight from "@mui/icons-material/ChevronRight";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { getStaffStats } from "../../services/staffAdminService";
import {
    PeriodKey, StaffUser, TeamLevelPerson, TeamLevelStats, TeamScopeStats,
} from "../../types/staff";

type Dataset = 'enrollments' | 'rides';

const PERIODS: { key: PeriodKey; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'month', label: 'This Month' },
    { key: 'allTime', label: 'All Time' },
];

const COLUMNS: Record<Dataset, [string, string, string]> = {
    enrollments: ['Enrolled', 'Approved', 'Pending'],
    rides: ['Requested', 'Completed', 'Cancelled'],
};

/** One scope's three numbers. null scope = the server declined a live rollup for a huge branch. */
export const tripleFor = (
    scope: TeamScopeStats | null | undefined,
    dataset: Dataset,
    period: PeriodKey,
): [number, number, number] | null => {
    if (!scope) return null;
    if (dataset === 'rides') {
        const r = scope.rides?.[period];
        return r ? [r.requested, r.completed, r.cancelled] : [0, 0, 0];
    }
    const p = scope.periods?.[period];
    return p ? [p.enrolled, p.approved, p.pending] : [0, 0, 0];
};

interface RowProps {
    person: TeamLevelPerson;
    depth: number;
    dataset: Dataset;
    period: PeriodKey;
    levels: Record<number, TeamLevelStats>;
    loading: Set<number>;
    expanded: Set<number>;
    onToggle: (id: number) => void;
}

const Nums: React.FC<{ triple: [number, number, number] | null; muted?: boolean }> = ({ triple, muted }) => (
    <>
        {(triple ?? ['—', '—', '—']).map((n, i) => (
            <TableCell key={i} align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: muted ? 'text.secondary' : undefined }}>
                {n}
            </TableCell>
        ))}
    </>
);

/** A person's branch line, then — once opened — their own line and each of their reports. */
const PersonRows: React.FC<RowProps> = (props) => {
    const { person, depth, dataset, period, levels, loading, expanded, onToggle } = props;
    const canExpand = person.directReports > 0;
    const isOpen = canExpand && expanded.has(person.staffId);
    const child = levels[person.staffId];

    return (
        <>
            <TableRow hover>
                <TableCell sx={{ pl: 1 + depth * 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {canExpand ? (
                            <IconButton size="small" onClick={() => onToggle(person.staffId)}>
                                {isOpen ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                            </IconButton>
                        ) : (
                            <Box sx={{ width: 30 }} />
                        )}
                        <Box>
                            <Typography variant="body2" fontWeight={600} component="span">
                                {person.name}
                            </Typography>
                            {person.teamSize > 1 && (
                                <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.75 }}>
                                    +{person.teamSize - 1}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block">
                                {person.title}
                                {person.status !== 'ACTIVE' ? ` · ${person.status}` : ''}
                            </Typography>
                        </Box>
                    </Box>
                </TableCell>
                <Nums triple={tripleFor(person.team, dataset, period)} />
            </TableRow>

            {isOpen && (
                <TableRow>
                    <TableCell sx={{ pl: 1 + (depth + 1) * 2.5 + 3.75, py: 0.75, color: 'text.secondary' }}>
                        <Typography variant="body2" fontStyle="italic">{person.name} (own)</Typography>
                    </TableCell>
                    <Nums triple={tripleFor(person.own, dataset, period)} muted />
                </TableRow>
            )}

            {isOpen && !child && loading.has(person.staffId) && (
                <TableRow>
                    <TableCell colSpan={4} sx={{ pl: 1 + (depth + 1) * 2.5 + 4 }}>
                        <CircularProgress size={16} />
                    </TableCell>
                </TableRow>
            )}

            {isOpen && child?.members.map((m) => (
                <PersonRows key={m.staffId} {...props} person={m} depth={depth + 1} />
            ))}
        </>
    );
};

interface Props {
    staff: StaffUser[];
}

/**
 * Per-person enrollment and booking numbers for any staff member and everyone under them.
 * A level at a time, so a large org costs no more to open than a small one.
 */
const StaffPerformancePanel: React.FC<Props> = ({ staff }) => {
    const [rootId, setRootId] = useState<number | null>(null);
    const [levels, setLevels] = useState<Record<number, TeamLevelStats>>({});
    const [loading, setLoading] = useState<Set<number>>(new Set());
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [dataset, setDataset] = useState<Dataset>('enrollments');
    const [period, setPeriod] = useState<PeriodKey>('today');
    const [error, setError] = useState<string | null>(null);

    // Default to whoever sits at the top of the org, so the panel opens on something useful.
    useEffect(() => {
        if (rootId === null && staff.length > 0) {
            setRootId((staff.find((s) => s.managerId == null) ?? staff[0]).id);
        }
    }, [staff, rootId]);

    const loadLevel = useCallback(async (id: number) => {
        setLoading((prev) => new Set(prev).add(id));
        try {
            const res = await getStaffStats(id);
            setLevels((prev) => ({ ...prev, [id]: res.data }));
            setError(null);
        } catch (e) {
            setError('Could not load these numbers.');
        } finally {
            setLoading((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    }, []);

    // Switching root starts a fresh exploration; cached levels stay valid and are reused.
    useEffect(() => {
        if (rootId != null && !levels[rootId]) loadLevel(rootId);
        setExpanded(new Set());
    }, [rootId]); // eslint-disable-line react-hooks/exhaustive-deps

    const onToggle = (id: number) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else {
                next.add(id);
                if (!levels[id]) loadLevel(id);
            }
            return next;
        });
    };

    const root = rootId != null ? levels[rootId] : undefined;
    const cols = COLUMNS[dataset];

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <Autocomplete
                    size="small"
                    sx={{ minWidth: 280 }}
                    options={staff}
                    value={staff.find((s) => s.id === rootId) ?? null}
                    getOptionLabel={(s) => `${s.firstName} ${s.lastName || ''}`.trim()}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, v) => setRootId(v?.id ?? null)}
                    renderInput={(params) => <TextField {...params} label="Start from" />}
                />
                <Tabs value={dataset} onChange={(_, v) => setDataset(v)}>
                    <Tab label="Enrollments" value="enrollments" />
                    <Tab label="Rides booked" value="rides" />
                </Tabs>
                <Tabs value={period} onChange={(_, v) => setPeriod(v)}>
                    {PERIODS.map((p) => <Tab key={p.key} label={p.label} value={p.key} />)}
                </Tabs>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Each row is that person's whole branch. Expand to see what they did themselves,
                then each of their reports.
            </Typography>
            {error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>}

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Person</TableCell>
                        {cols.map((c) => <TableCell key={c} align="right">{c}</TableCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {root && (
                        <>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell sx={{ pl: 1 }}>
                                    <Tooltip title="This person alone, not their team">
                                        <Typography variant="body2" fontWeight={700}>
                                            {root.manager.name} (own)
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                <Nums triple={tripleFor(root.manager.own, dataset, period)} />
                            </TableRow>
                            {root.members.map((m) => (
                                <PersonRows
                                    key={m.staffId}
                                    person={m}
                                    depth={0}
                                    dataset={dataset}
                                    period={period}
                                    levels={levels}
                                    loading={loading}
                                    expanded={expanded}
                                    onToggle={onToggle}
                                />
                            ))}
                            <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                                <TableCell sx={{ pl: 1 }}>Everyone under {root.manager.name}</TableCell>
                                <Nums triple={tripleFor(root.manager.team, dataset, period)} />
                            </TableRow>
                        </>
                    )}
                    {!root && rootId != null && (
                        <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                <CircularProgress size={22} />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Box>
    );
};

export default StaffPerformancePanel;
